import {
  AmountSumTable,
  AmountSumTableItem,
  AnnualIncomeWidget,
  displayCashAccountType,
  displayInvestmentType,
  displayLoanType,
  displayRealEstateType,
  getLoanBalance,
  Icon,
  monetaryAmountDataPointValue,
  ScorecardComponent,
  ttDetailsKeys,
  useElementsEnvironmentContext,
  useTtDetails,
} from '@elements-financial/elements';
import { useEffect, useState } from 'react';
import * as Bridge from '../modules/bridge';
import { useTranslation } from 'react-i18next';
import { ChartCard } from '~/components/ChartCard';
import { useQueryClient } from '@tanstack/react-query';
import { Console } from '~/modules/console';
import { UpdatingAccountsNotification } from '~/components/UpdatingAccountsNotification';

declare global {
  interface Window {
    Bridge: typeof Bridge;
  }
}

export default function Index() {
  useEffect(() => {
    Console.log('Setting Bridge on window');
    window.Bridge = Bridge;
    document.dispatchEvent(new CustomEvent('elements:load'));
    const noZoomTag = document.createElement('meta');
    noZoomTag.name = 'viewport';
    noZoomTag.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0';
    document.head.appendChild(noZoomTag);
  }, []);

  const { householdID } = useElementsEnvironmentContext();

  const { ttDetails, ttDetailsQuery } = useTtDetails({
    householdID: householdID,
    includeAssetSummary: true,
    includeDebtSummary: true,
  });

  const { t: tHome } = useTranslation('home');
  const { t: tCashAccount } = useTranslation('cashAccount');
  const { t: tInvestment } = useTranslation('investment');
  const { t: tRealEstate } = useTranslation('realEstate');
  const { t: tLoan } = useTranslation('loan');

  const [updatesInFlight, setUpdatesInFlight] = useState(0);
  const queryClient = useQueryClient();
  useEffect(() => {
    Bridge.onChange('ttDetails', (state) => {
      Console.log('ttDetails change handler called with state:', state);
      switch (state) {
        case 'pending':
          setUpdatesInFlight(n => n + 1);
          break;
        case 'complete':
          queryClient.invalidateQueries(
            ttDetailsKeys.ttDetails({
              householdID: householdID,
              includeAssetSummary: true,
              includeDebtSummary: true
            }))
            .then(() => { setUpdatesInFlight(n => n - 1); })
            .catch(() => { setUpdatesInFlight(n => n - 1); });
          break;
      }
    });
    return () => { Bridge.unregister('ttDetails'); };
  }, [householdID, queryClient]);

  if (householdID === undefined || householdID === null) {
    return (<></>);
  }

  return (
    <div>
      {updatesInFlight > 0 && <UpdatingAccountsNotification />}
      <div className="flex flex-col space-y-6 p-4">
        <ScorecardComponent
          householdID={householdID}
          scorecardElementPropOverrides={{ withFullName: true, withCompactName: false }}
          onScorecardElementClick={(symbol) => {
            Bridge.routeTo(symbol);
          }}
        />
        <ChartCard householdID={householdID} className={'self-center'} />
        <AnnualIncomeWidget
          householdID={householdID}
          title={tHome('total-household-income')}
          onClick={() => {
            Bridge.routeTo('annualIncome');
          }}
        />
        <AmountSumTable
          isLoading={ttDetailsQuery.isLoading}
          sum={ttDetails?.assetSummary.totalAssets}
          hideOnEmpty={false}
          title={tHome('assets-title').toUpperCase()}
          sumTitle={tHome('assets-total')}
        >
          {ttDetails?.assetSummary.cashAccounts
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((cashAccount) => (
              <AmountSumTableItem
                key={cashAccount.id}
                title={cashAccount.name}
                amount={monetaryAmountDataPointValue(cashAccount.value.latestDataPoint)}
                subtitle={displayCashAccountType(cashAccount.cashAccountType, tCashAccount)}
                withChevron={true}
                linkStatus={cashAccount.linkStatus}
                onClick={() => {
                  Console.log(cashAccount.linkStatus);
                  Bridge.routeTo(`netWorthItem/${cashAccount.id}`);
                }}
              />
            ))
            .concat(
              ttDetails?.assetSummary.preTaxInvestments
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((investment) => (
                  <AmountSumTableItem
                    key={investment.id}
                    title={investment.name}
                    amount={monetaryAmountDataPointValue(investment.value.latestDataPoint)}
                    subtitle={displayInvestmentType(investment.investmentType, tInvestment)}
                    withChevron={true}
                    linkStatus={investment.linkStatus}
                    onClick={() => {
                      Console.log(investment.linkStatus);
                      Bridge.routeTo(`netWorthItem/${investment.id}`);
                    }}
                  />
                ))
            )
            .concat(
              ttDetails?.assetSummary.afterTaxInvestments
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((investment) => (
                  <AmountSumTableItem
                    key={investment.id}
                    title={investment.name}
                    amount={monetaryAmountDataPointValue(investment.value.latestDataPoint)}
                    subtitle={displayInvestmentType(investment.investmentType, tInvestment)}
                    withChevron={true}
                    linkStatus={investment.linkStatus}
                    onClick={() => {
                      Console.log(investment.linkStatus);
                      Bridge.routeTo(`netWorthItem/${investment.id}`);
                    }}
                  />
                ))
            )
            .concat(
              ttDetails?.assetSummary.personalRealEstate
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((realEstate) => (
                  <AmountSumTableItem
                    key={realEstate.id}
                    title={realEstate.name}
                    amount={monetaryAmountDataPointValue(realEstate.value.latestDataPoint)}
                    subtitle={displayRealEstateType(tRealEstate, realEstate.realEstateType)}
                    withChevron={true}
                    onClick={() => {
                      Bridge.routeTo(`netWorthItem/${realEstate.id}`);
                    }}
                  />
                ))
            )
            .concat(
              ttDetails?.assetSummary.investmentRealEstate
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((realEstate) => (
                  <AmountSumTableItem
                    key={realEstate.id}
                    title={realEstate.name}
                    amount={monetaryAmountDataPointValue(realEstate.value.latestDataPoint)}
                    subtitle={displayRealEstateType(tRealEstate, realEstate.realEstateType)}
                    withChevron={true}
                    onClick={() => {
                      Bridge.routeTo(`netWorthItem/${realEstate.id}`);
                    }}
                  />
                ))
            )
            .concat(
              ttDetails?.assetSummary.personalPropertyItems
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((item) => (
                  <AmountSumTableItem
                    key={item.id}
                    title={item.name}
                    amount={monetaryAmountDataPointValue(item.value.latestDataPoint)}
                    withChevron={true}
                    onClick={() => {
                      Bridge.routeTo(`netWorthItem/${item.id}`);
                    }}
                  />
                ))
            )
            .concat(
              ttDetails?.assetSummary.noteReceivables
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((note) => (
                  <AmountSumTableItem
                    key={note.id}
                    title={note.name}
                    amount={monetaryAmountDataPointValue(note.value.latestDataPoint)}
                    withChevron={true}
                    onClick={() => {
                      Bridge.routeTo(`netWorthItem/${note.id}`);
                    }}
                  />
                ))
            )
            .concat(
              ttDetails?.assetSummary.businesses
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((business) => (
                  <AmountSumTableItem
                    key={business.id}
                    title={business.name}
                    amount={monetaryAmountDataPointValue(business.value.latestDataPoint)}
                    subtitle={tHome('business-subtitle')}
                    withChevron={true}
                    onClick={() => {
                      Bridge.routeTo(`netWorthItem/${business.id}`);
                    }}
                  />
                ))
            )}
        </AmountSumTable>
        <AmountSumTable
          className="pb-[7px]"
          isLoading={ttDetailsQuery.isLoading}
          sum={ttDetails?.debtSummary.totalDebt}
          hideOnEmpty={false}
          title={tHome('debts-title').toUpperCase()}
          sumTitle={tHome('debts-total')}
        >
          {ttDetails?.debtSummary.personalRealEstateLoans
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((debt) => (
              <AmountSumTableItem
                key={debt.id}
                title={debt.name}
                amount={getLoanBalance(debt)}
                subtitle={displayLoanType(debt.loanType, tLoan)}
                withChevron={true}
                linkStatus={debt.linkStatus}
                onClick={() => {
                  Bridge.routeTo(`netWorthItem/${debt.id}`);
                }}
              />
            ))
            .concat(
              ttDetails?.debtSummary.investmentRealEstateLoans
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((debt) => (
                  <AmountSumTableItem
                    key={debt.id}
                    title={debt.name}
                    amount={getLoanBalance(debt)}
                    subtitle={displayLoanType(debt.loanType, tLoan)}
                    withChevron={true}
                    linkStatus={debt.linkStatus}
                    onClick={() => {
                      Bridge.routeTo(`netWorthItem/${debt.id}`);
                    }}
                  />
                ))
            )
            .concat(
              ttDetails?.debtSummary.otherDebt
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((debt) => (
                  <AmountSumTableItem
                    key={debt.id}
                    title={debt.name}
                    amount={getLoanBalance(debt)}
                    subtitle={displayLoanType(debt.loanType, tLoan)}
                    withChevron={true}
                    linkStatus={debt.linkStatus}
                    onClick={() => {
                      Bridge.routeTo(`netWorthItem/${debt.id}`);
                    }}
                  />
                ))
            )
            .concat(
              ttDetails?.debtSummary.businessLoans
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((debt) => (
                  <AmountSumTableItem
                    key={debt.id}
                    title={debt.name}
                    amount={getLoanBalance(debt)}
                    subtitle={displayLoanType(debt.loanType, tLoan)}
                    withChevron={true}
                    linkStatus={debt.linkStatus}
                    onClick={() => {
                      Bridge.routeTo(`netWorthItem/${debt.id}`);
                    }}
                  />
                ))
            )
            .concat(
              ttDetails?.debtSummary.studentLoans
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((debt) => (
                  <AmountSumTableItem
                    key={debt.id}
                    title={debt.name}
                    amount={getLoanBalance(debt)}
                    subtitle={displayLoanType(debt.loanType, tLoan)}
                    withChevron={true}
                    linkStatus={debt.linkStatus}
                    onClick={() => {
                      Bridge.routeTo(`netWorthItem/${debt.id}`);
                    }}
                  />
                ))
            )}
        </AmountSumTable>
        <div
          className="rounded-[16px] p-5 shadow-mobileTile bg-darkMode-gray-mobileTile"
          onClick={() => {
            Bridge.routeTo('reports');
          }}
        >
          <div className="flex items-center space-x-4 h-6">
            <Icon icon="barChartFilled" className="w-6 h-6 text-darkMode-gray-medium" />
            <div className="flex-1 text-[18px] font-normal tracking-[-0.18px] text-white">{tHome('reports-title')}</div>
            <Icon icon="chevronRight" className="w-6 h-6 text-palette-600" />
          </div>
        </div>
        <div className="h-20" />
      </div>
    </div>
  );
}
